import { Location } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { BasketService } from '@basket';
import { CheckoutRoutes } from '@checkout';
import { JourneyStepTrackerService } from '@common';
import {
  GridColumnComponent,
  GridContainerComponent,
  GridRowComponent,
  ProgressionStepsComponent,
} from '@components';
import { CustomerService } from '@customer';
import {
  BasketComponent,
  BasketStickyWrapperComponent,
  GADataService,
  OverlayService,
  OverlayState,
} from '@data-access';
import { ErrorRoutes } from '@features/error';
import { RegistrationConfirmationRoutes } from '@features/registration-confirmation';
import { GAJourneyVersion } from '@ga';
import { provideTranslationDictionaryMapping, TranslatePipe } from '@i18n';
import { PosthogTrackingEvents, TrackingService } from '@tracking';
import posthog from 'posthog-js';
import { catchError, finalize, tap } from 'rxjs';
import { ContactDetailsFormComponent } from '../../components/contact-details-form/contact-details-form.component';
import { translation } from '../../i18n/translation.constant';
import { ContactDetailsFormValue } from '../../models/contact-details/contact-details-form-value.type';
import { RegistrationService } from '../../services/registration/registration.service';

@Component({
  selector: 'app-contact-details',
  imports: [
    BasketComponent,
    BasketStickyWrapperComponent,
    ContactDetailsFormComponent,
    GridColumnComponent,
    GridContainerComponent,
    GridRowComponent,
    ProgressionStepsComponent,
    TranslatePipe,
  ],
  templateUrl: './contact-details.component.html',
  styleUrl: './contact-details.component.scss',
  providers: [provideTranslationDictionaryMapping(translation)],
})
export class ContactDetailsComponent implements OnInit {
  private readonly basketService = inject(BasketService);
  private readonly customerService = inject(CustomerService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly gaDataService = inject(GADataService);
  private readonly journeyStepTracker = inject(JourneyStepTrackerService);
  private readonly location = inject(Location);
  private readonly overlayService = inject(OverlayService);
  private readonly trackingService = inject(TrackingService);
  private readonly registrationService = inject(RegistrationService);
  private readonly router = inject(Router);

  readonly customerHasSelectedToPurchasePlan =
    this.customerService.customerSelectedToPurchasePlan;

  ngOnInit(): void {
    const quoteSucceeded = Boolean(this.basketService.retrievedQuoteId());
    const stepNumber = quoteSucceeded ? 3 : 2;
    const totalSteps = quoteSucceeded ? 3 : 2;
    this.journeyStepTracker.updateStep(
      stepNumber,
      totalSteps,
      'Your details',
      false,
    );

    this.customerService.setCustomerMarketingPreferences({
      email: false,
      post: true,
      telephone: false,
    });

    switch (this.gaDataService.journeyVersion()) {
      case GAJourneyVersion.MultiPlan:
        posthog.capture(PosthogTrackingEvents.MultiPlanContactDetailsLoaded);
        break;
      case GAJourneyVersion.SinglePlan:
        posthog.capture(PosthogTrackingEvents.SinglePlanContactDetailsLoaded);
        break;
      case GAJourneyVersion.RegisterOnly:
        posthog.capture(PosthogTrackingEvents.RegOnlyContactDetailsLoaded);
        break;
    }
  }

  navigateBack(): void {
    this.location.back();
  }

  onFormSubmit(formValue: ContactDetailsFormValue): void {
    this.customerService.setCustomerEmailAddress(formValue.email);
    this.customerService.setCustomerPhoneNumber(formValue.phone);

    const hasEmail = Boolean(formValue.email);
    const isRegOnly = !this.customerHasSelectedToPurchasePlan();

    const emailConsent =
      hasEmail && (isRegOnly || Boolean(formValue.marketingPreferenceEmail));

    this.customerService.setCustomerMarketingPreferences({
      post: Boolean(!formValue.marketingPreferencePost),
      email: emailConsent,
      telephone: Boolean(formValue.phone),
    });

    this.trackingService.trackEvent({
      ...this.gaDataService.preQuoteAppliance(),
      eventAction: 'submit form: true',
      eventCategory: 'contact details form',
      eventLabel: 'success',
      journeyType: this.gaDataService.journeyVersion(),
      multiPlanOffer: this.gaDataService.multiPlanOffer(),
      offerStatus: this.gaDataService.offerStatus(),
    });

    switch (this.gaDataService.journeyVersion()) {
      case GAJourneyVersion.MultiPlan:
        posthog.capture(PosthogTrackingEvents.MultiPlanRegistrationSubmitted);
        break;
      case GAJourneyVersion.SinglePlan:
        posthog.capture(PosthogTrackingEvents.SinglePlanRegistrationSubmitted);
        break;
      case GAJourneyVersion.RegisterOnly:
        posthog.capture(PosthogTrackingEvents.RegOnlyRegistrationSubmitted);
        break;
    }

    this.overlayService.show(OverlayState.Register);

    this.registrationService
      .registerAppliance()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => {
          this.trackingService.trackEvent(
            this.gaDataService.eventRegistrationStored(),
          );

          switch (this.gaDataService.journeyVersion()) {
            case GAJourneyVersion.MultiPlan:
              posthog.capture(
                PosthogTrackingEvents.MultiPlanRegistrationSubmittedSuccess,
              );
              break;
            case GAJourneyVersion.SinglePlan:
              posthog.capture(
                PosthogTrackingEvents.SinglePlanRegistrationSubmittedSuccess,
              );
              break;
            case GAJourneyVersion.RegisterOnly:
              posthog.capture(
                PosthogTrackingEvents.RegOnlyRegistrationSubmittedSuccess,
                {
                  wasEmailCaptured: hasEmail,
                },
              );
              break;
          }

          if (this.customerHasSelectedToPurchasePlan()) {
            this.router.navigate([CheckoutRoutes.DirectDebit]);
          } else {
            this.router.navigate([
              RegistrationConfirmationRoutes.RegistrationConfirmation,
            ]);
          }
        }),
        catchError(() => {
          this.trackingService.trackEvent({
            ...this.gaDataService.preQuoteAppliance(),
          });
          return this.router.navigate([ErrorRoutes.Error]);
        }),
        finalize(() => this.overlayService.hide()),
      )
      .subscribe();
  }
}
